import { Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import ChromeSuraasaIcon from "assets/Cpat/chrome_suraasa_icon.webp"
import clsx from "clsx"
import FullPageLoading from "components/FullPageLoading"
import ProctoringDuringAssessment from "features/Cpat/Assessments/Instructions/ProctoringDuringAssessment"
import { CpatFaqs } from "features/Cpat/CpatOverview/Faqs"
import { useGlobalContext } from "global/Context/context"
import {
  cpatDurationInMinutes,
  cpatPattern,
  cpatQuestions,
} from "utils/constants"
import { pluralize } from "utils/helpers"
import { useEnrollments } from "utils/hooks/useEnrollments"

import CpatStartAssessmentBanner from "./CpatStartAssessmentBanner"

const QuestionTypeInstruction = ({
  boxClassName,
  text,
  digitClassName,
}: {
  boxClassName?: string
  text: string
  digitClassName?: string
}) => {
  return (
    <div className="flex flex-row items-center gap-1">
      <div
        className={clsx(
          "flex size-5 items-center justify-center rounded-lg border-2 border-secondary-500 bg-secondary-50 p-1",
          boxClassName
        )}
      >
        <Typography
          variant={"strong"}
          className={clsx("text-onSurface-700", digitClassName)}
        >
          1
        </Typography>
      </div>
      <Typography variant={"body"} className="text-onSurface-700">
        {text}
      </Typography>
    </div>
  )
}

const CpatStartDashboard = () => {
  const { cpat } = useEnrollments()
  const { isNcte } = useGlobalContext()

  const { data, isLoading } = useQuery({
    queryKey: ["cpat-assessment", cpat.latestCPAT?.id],
    queryFn: () =>
      api.cpat.getAssessment({ params: { assessmentId: cpat.latestCPAT?.id } }),
    enabled: !!cpat.latestCPAT?.id,
  })

  if (isLoading) {
    return <FullPageLoading debugMessage="Loading CPAT Details" />
  }

  const AssessmentFullTitle = isNcte
    ? "National Council for Teacher Education Assessment"
    : "Comprehensive Pedagogy Assessment of Teachers"

  const AssessmentFullDescription = isNcte
    ? "National Council for Teacher Education Assessment"
    : "Comprehensive Pedagogy Assessment of Teachers (CPAT)"

  return (
    <div>
      <CpatStartAssessmentBanner />

      <Typography variant="title2" className="mb-2.5">
        What is {AssessmentFullTitle}?
      </Typography>
      <Typography variant="largeBody" className="mb-5 text-onSurface-600">
        The {AssessmentFullDescription} is an exciting opportunity for you to
        assess your pedagogical skills. During a one-hour online assessment, you
        get the opportunity to put your expertise to test. Based on your
        performance, you receive a customized pedagogy report — highlighting
        what you’re good at & where you can improve. This customized feedback
        guides you toward becoming an even more effective educator. You can
        attempt the assessment without the fear of getting judged. Your report
        will be 100% confidential & only accessible to you.
      </Typography>
      <div className="mb-7 flex flex-wrap items-center justify-between gap-3">
        <div className="md:w-1/2">
          <Typography variant="title2" className="mb-2">
            Syllabus & Pattern
          </Typography>
          <Typography variant="largeBody" className="text-onSurface-600">
            All questions would be based around teaching skills and pedagogy.
            The assessment is not subject-specific. Any teacher can attempt the
            assessment irrespective of the subject they teach.
          </Typography>
        </div>
        <div className="flex shrink-0 items-center justify-center gap-1">
          <div className="rounded-lg bg-[#F8EFFF] px-3 py-2 text-center">
            <div className="flex flex-col gap-1.5">
              <Typography
                variant={"strongSmallBody"}
                className="text-xs text-[#765B8B]"
              >
                Duration
              </Typography>
              <Typography variant={"title2"} className="text-3xl font-bold">
                {pluralize("hr", cpatDurationInMinutes / 60)}
              </Typography>
            </div>
          </div>
          <div className="rounded-lg bg-[#F8EFFF] px-3 py-2 text-center">
            <div className="flex flex-col gap-1.5">
              <Typography
                variant={"strongSmallBody"}
                className="text-xs text-[#765B8B]"
              >
                Questions
              </Typography>
              <Typography variant={"title2"} className="text-3xl font-bold">
                {cpatQuestions}
              </Typography>
            </div>
          </div>
          <div className="rounded-lg bg-[#F8EFFF] px-3 py-2 text-center">
            <div className="flex flex-col gap-1.5">
              <Typography
                variant={"strongSmallBody"}
                className="text-xs text-[#765B8B]"
              >
                Pattern
              </Typography>
              <Typography variant={"title2"} className="text-3xl font-bold">
                {cpatPattern}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <Typography variant="title2" className="mb-2">
        How do I Prepare?
      </Typography>
      <Typography variant="largeBody" className="mb-8 text-onSurface-600">
        The purpose of the {AssessmentFullDescription} is to assess your
        existing pedagogical knowledge and skills. No prior preparation is
        required. This enables you to get unbiased and accurate expert
        recommendations in your report.
      </Typography>
      <Typography
        variant="title2"
        className="mb-4 scroll-mt-12"
        id="general-instructions"
      >
        General Instructions for the Assessment
      </Typography>
      <ol className="mb-3 ml-4 list-decimal text-onSurface-600">
        <li>
          <Typography variant={"largeBody"} className="text-onSurface-600">
            The assessment consists of a total of {cpatQuestions} questions.
          </Typography>
        </li>
        <li>
          <Typography variant={"largeBody"} className="text-onSurface-600">
            The total duration of the test is {cpatDurationInMinutes / 60} hour
            ({cpatDurationInMinutes} minutes).
          </Typography>
        </li>
        <li>
          <Typography variant={"largeBody"} className="text-onSurface-600">
            There is no negative marking for incorrect answers.
          </Typography>
        </li>
        <li>
          <Typography variant={"largeBody"} className="text-onSurface-600">
            Using any external means to seek answers is strictly prohibited.
          </Typography>
        </li>
        <li>
          <Typography variant={"largeBody"} className="text-onSurface-600">
            Failure to submit the test before the time runs out will result in
            automatic submission.
          </Typography>
        </li>
      </ol>
      <Typography variant={"body"} className="mb-2 text-onSurface-700">
        The Question Panel displayed on the screen will indicate the status of
        each question using one of the following symbols:
      </Typography>
      <div className="mb-7 flex flex-wrap items-center justify-between gap-2">
        <QuestionTypeInstruction text="Questions Not Yet Visited" />
        <QuestionTypeInstruction
          text="Questions Answered"
          boxClassName="bg-success-500 border-none "
          digitClassName="!text-white"
        />
        <QuestionTypeInstruction
          text="Questions Not Answered"
          boxClassName="bg-warning-500  border-none"
          digitClassName="!text-white"
        />
      </div>
      {data?.assessment.isProctored === true && (
        <div className="mb-4">
          <ProctoringDuringAssessment maxWarningCount={10} />
        </div>
      )}
      <Typography variant="title3" className="mb-3.5">
        Syllabus & Pattern
      </Typography>
      <Typography variant="strong" className="mb-1 ">
        Multiple Choice Questions
      </Typography>
      <Typography variant="body" className="mb-2 ">
        Each question will have 4 options of which only one can be selected as
        the answer. Your answer is automatically saved upon selecting an option.
      </Typography>
      <Typography variant="body" className="text-onSurface-600">
        Your answer is automatically saved upon selecting an option.
      </Typography>
      <div className="my-8 flex flex-wrap gap-2 rounded-2xl bg-surface-100 p-2.5">
        <img src={ChromeSuraasaIcon} alt="" className="h-6" />
        <div className="flex flex-col gap-1">
          <Typography variant={"title4"}>
            Please use Google Chrome or Suraasa app for the assessment
          </Typography>
          <Typography variant={"smallBody"} className="text-onSurface-600">
            For optimal performance and to avoid technical issues, we recommend
            completing the assessment on the latest version of Google Chrome or
            Suraasa app. Using browsers other than Google Chrome may lead to
            compatibility issues during the assessment.
          </Typography>
        </div>
      </div>
      <CpatFaqs assessmentCompleted={false} />
    </div>
  )
}

export default CpatStartDashboard
